import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import { authActions, authSelectors } from 'state/auth';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { progressBarActions } from 'state/progressBar';
import { serviceTypeActions } from 'state/serviceType';
import DefaultModal from 'components/Modal/Modal';
import { workflowActions } from 'state/workflow';
import './NameHeader.scss';
import xywavXyremLogo from '../../assets/xywavXyremLogo.svg';
import { Nav, Navbar, NavDropdown } from 'react-bootstrap';

interface NameHeaderProps {
  serviceType: 'xyrem' | 'xywav' | false;
  signOut?: true;
  remsFlow?: boolean;
  disableLogoOnClick?: boolean;
}

const LOGO_SELECT_MED_TEXT =
  'You are about to be taken back to the drug selection screen. Any data entered so far will be discarded. Are you sure?';
const LOGO_BACK_TO_LOGIN =
  'You are about to be taken back to the login page. Any data entered will be discarded. Are you sure?';

type backToType = 'chooseMed' | 'logout' | '';
type showConfirmationType = {
  show: boolean;
  backTo: backToType;
};

const NameHeader = ({ serviceType, signOut, remsFlow, disableLogoOnClick }: NameHeaderProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(authSelectors.userSelector);
  const isDisableLogo = disableLogoOnClick || (!serviceType && signOut);

  const token = useAppSelector(authSelectors.tokenSelector);

  const [showConfirmation, setShowConfirmation] = useState<showConfirmationType>({
    show: false,
    backTo: '',
  });
  const [showPdf, setShowPdf] = useState(false);
  const [modalText, setModalText] = useState('');

  const { show, backTo } = showConfirmation;

  const handleLogOut = async () => {
    await dispatch(authActions.logOutAndRemoveRefreshToken({ token: token }));
    dispatch(workflowActions.setError(false));
    dispatch(progressBarActions.resetStep());
    navigate('/');
  };

  const displayConfirmationModal = (backTo: backToType) => {
    const text = backTo === 'chooseMed' ? LOGO_SELECT_MED_TEXT : LOGO_BACK_TO_LOGIN;
    setModalText(text);
    setShowConfirmation({ show: true, backTo });
  };

  const displayPdf = () => {
    setShowPdf(true);
  };

  const handleReset = () => {
    if (remsFlow && backTo === 'chooseMed') {
      navigate('/workflow', {
        state: {
          reset: true,
        },
      });
    } else {
      handleLogOut();
    }

    dispatch(serviceTypeActions.setServiceType(''));
    dispatch(progressBarActions.resetStep());
  };

  const handleClose = () => {
    setShowConfirmation({ show: false, backTo: '' });
    setShowPdf(false);
  };

  const setBackTo = signOut ? 'chooseMed' : 'logout';

  return (
    <Navbar className="navbar-shadow px-2 px-sm-3 px-md-5 flex-column align-items-center align-items-md-start flex-md-row d-flex">
      <DefaultModal
        handleClose={handleClose}
        handleSubmit={handleReset}
        show={show}
        text={modalText}
        cancelButtonText="Cancel"
        continueButtonText="Yes, Continue"
      />

      <Navbar.Brand>
        <img
          onClick={() => {
            if (isDisableLogo) {
              return;
            }
            displayConfirmationModal(setBackTo);
          }}
          id="home-button-image-header"
          role={isDisableLogo ? undefined : 'button'}
          height={40}
          src={require('./../../assets/logo/jazz_logo.png')}
        />
      </Navbar.Brand>

      <Nav className="ms-md-auto ms-0 mt-2">
        {serviceType && (
          <>
            <a
              onClick={displayPdf}
              id="show-pdf"
              className="text-decoration-none me-2 my-auto text-purple"
              style={{ cursor: 'pointer', fontSize: '14px' }}
            >
              Prescribing&nbsp;Information
            </a>
            <img
              data-testid={serviceType}
              src={require(`./../../assets/logo/${serviceType}_logo.png`)}
              height="30"
              className="mx-2"
              style={{ marginTop: '2px' }}
            />
            <Modal
              className="modal-xl"
              show={showPdf}
              onHide={handleClose}
              dialogClassName="modal-fixed-width"
            >
              <Modal.Header className="p-2">
                <Modal.Title className="mb-0">
                  <h4>{serviceType && serviceType.toUpperCase()} REMS</h4>
                </Modal.Title>

                <button
                  type="button"
                  className="btn-close"
                  onClick={handleClose}
                ></button>
              </Modal.Header>
              <iframe
                src={`https://pp.jazzpharma.com/pi/${serviceType}.en.USPI.pdf`}
                style={{ width: '100%', height: '80vh' }}
              ></iframe>
            </Modal>
          </>
        )}
        {!serviceType && (
          <img
            src={xywavXyremLogo}
            className="me-2 my-2"
          />
        )}
        {signOut && (
          <NavDropdown
            title={
              <div className="user-initials">
                {user && user.first_name.slice(0, 1) + user.last_name.slice(0, 1)}
              </div>
            }
            drop="down"
            align="end"
            className="user-initials-dropdown"
            data-testid="logout-button"
          >
            <NavDropdown.Item
              id="sign-out-button"
              data-testid="logout-button"
              className="btn btn-link"
              onClick={() => displayConfirmationModal('logout')}
            >
              Sign out
            </NavDropdown.Item>
          </NavDropdown>
        )}
      </Nav>
    </Navbar>
  );
};

export default NameHeader;
